import { useEffect, useId, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useFocusTrap } from '../lib/useFocusTrap.js';
import { Button } from './Button.js';
import { Spinner } from './Spinner.js';
import { controlFocus } from '../primitives/shared.js';

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  description?: string;
  loading?: boolean;
  error?: string;
  /**
   * A destructive confirmation requires an explicit choice, so `Esc` does not
   * close it (07-11 per-component criteria, PRM-25).
   */
  destructive?: boolean;
  footer?: ReactNode | null;
  children?: ReactNode;
  className?: string;
  /**
   * `bottom-sheet` docks to the bottom below the md breakpoint and centres
   * from `md` up (07-05 age-restricted sheet). Default stays centred.
   */
  placement?: 'center' | 'bottom-sheet';
  /** Icon close control in the header (non-destructive dialogs only). */
  showCloseButton?: boolean;
}

/** PRM-25 Dialog — interrupt for a decision. */
export function Dialog({
  open,
  title,
  onClose,
  description,
  loading,
  error,
  destructive,
  footer,
  children,
  className,
  placement = 'center',
  showCloseButton = false,
}: DialogProps) {
  const uid = useId().replace(/:/g, '');
  const titleId = `${uid}-title`;
  const descriptionId = description ? `${uid}-description` : undefined;
  const ref = useFocusTrap<HTMLDivElement>(open);

  useEffect(() => {
    if (!open || destructive) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, destructive, onClose]);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[var(--z-dialog)] flex bg-overlay p-field',
        placement === 'bottom-sheet'
          ? 'items-end justify-center md:items-center'
          : 'items-center justify-center',
      )}
      data-placement={placement}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !destructive) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={loading || undefined}
        data-primitive="PRM-25"
        tabIndex={-1}
        className={cn(
          'flex max-h-[90vh] w-full flex-col gap-field overflow-auto bg-raised p-field shadow-dialog',
          placement === 'bottom-sheet'
            ? 'max-w-lg rounded-t-2xl md:rounded-overlay max-md:max-w-none max-md:rounded-b-none'
            : 'max-w-lg rounded-overlay',
          controlFocus,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-inline">
          <h2 id={titleId} className="min-w-0 flex-1 text-h2 font-bold text-default">
            {title}
          </h2>
          {showCloseButton && !destructive ? (
            <button
              type="button"
              className={cn(
                'inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-control text-muted hov:bg-sunken',
                controlFocus,
              )}
              aria-label={`Close ${title}`}
              onClick={onClose}
            >
              <span aria-hidden="true" className="text-h3 leading-none">
                ×
              </span>
            </button>
          ) : null}
        </div>
        {description ? (
          <p id={descriptionId} className="text-body text-muted">
            {description}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-body-sm text-danger">
            {error}
          </p>
        ) : null}
        {loading ? <Spinner label={`Loading ${title}`} /> : children}
        {footer === null ? null : (
          <div className="flex justify-end gap-inline">
            {footer ?? (
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
